import {
  AssetPlacementType,
  FundinfoPricingFrequency,
  InstrumentMutualFundLegalStatus,
  StructuredProductType,
} from '@Common/types/bo/instruments';
import {
  B2bInstrumentAssetType,
  InstrumentAssetType,
  InstrumentManagementType,
  InstrumentMutualFundAssetClass,
} from '@Common/types/instruments/instrument';

import { makeChoicesCommon } from '@sh4res/bo-common/utils';

export const mutualFundAssetClassChoices = makeChoicesCommon<InstrumentMutualFundAssetClass>()({
  diversified: 'resources.bo_common.instruments.mutual_fund_asset_class.diversified',
  equity: 'resources.bo_common.instruments.mutual_fund_asset_class.equity',
  bond: 'resources.bo_common.instruments.mutual_fund_asset_class.bond',
  money_market: 'resources.bo_common.instruments.mutual_fund_asset_class.money_market',
  real_estate: 'resources.bo_common.instruments.mutual_fund_asset_class.real_estate',
});

export const managementTypeChoices = makeChoicesCommon<InstrumentManagementType>()({
  active: 'resources.bo_common.instruments.management_type.active',
  passive: 'resources.bo_common.instruments.management_type.passive',
});

export const legalStatusChoices = makeChoicesCommon<InstrumentMutualFundLegalStatus>()({
  FCP: 'resources.bo_common.instruments.legal_status.FCP',
  FCPE: 'resources.bo_common.instruments.legal_status.FCPE',
  SICAV: 'resources.bo_common.instruments.legal_status.SICAV',
  SPPICAV: 'resources.bo_common.instruments.legal_status.SPPICAV',
  MONETAIRE: 'resources.bo_common.instruments.legal_status.MONETAIRE',
  SICAF: 'resources.bo_common.instruments.legal_status.SICAF',
  FCIMT: 'resources.bo_common.instruments.legal_status.FCIMT',
  PIF: 'resources.bo_common.instruments.legal_status.PIF',
  FCPI: 'resources.bo_common.instruments.legal_status.FCPI',
  CIPF: 'resources.bo_common.instruments.legal_status.CIPF',
  structured_products: 'resources.bo_common.instruments.legal_status.structured_products',
  euro_fund_life_insurance_scheme:
    'resources.bo_common.instruments.legal_status.euro_fund_life_insurance_scheme',
  FCPR: 'resources.bo_common.instruments.legal_status.FCPR',
  unit_trust: 'resources.bo_common.instruments.legal_status.unit_trust',
  OEIC: 'resources.bo_common.instruments.legal_status.OEIC',
  ICVC: 'resources.bo_common.instruments.legal_status.ICVC',
  OPCI: 'resources.bo_common.instruments.legal_status.OPCI',
  scpi_real_estate: 'resources.bo_common.instruments.legal_status.scpi_real_estate',
  ICAV: 'resources.bo_common.instruments.legal_status.ICAV',
  FPVG: 'resources.bo_common.instruments.legal_status.FPVG',
  FIAR: 'resources.bo_common.instruments.legal_status.FIAR',
  FPCI: 'resources.bo_common.instruments.legal_status.FPCI',
  open_ended_umbrella_unit_trust:
    'resources.bo_common.instruments.legal_status.open_ended_umbrella_unit_trust',
  cayman_ltd: 'resources.bo_common.instruments.legal_status.cayman_ltd',
  LLC: 'resources.bo_common.instruments.legal_status.LLC',
  SCI: 'resources.bo_common.instruments.legal_status.SCI',
  FPS: 'resources.bo_common.instruments.legal_status.FPS',
  FIC: 'resources.bo_common.instruments.legal_status.FIC',
  OFS: 'resources.bo_common.instruments.legal_status.OFS',
  FFS: 'resources.bo_common.instruments.legal_status.FFS',
  SCA: 'resources.bo_common.instruments.legal_status.SCA',
  FCC: 'resources.bo_common.instruments.legal_status.FCC',
  SCP: 'resources.bo_common.instruments.legal_status.SCP',
  ELTIF: 'resources.bo_common.instruments.legal_status.ELTIF',
  internal_fund: 'resources.bo_common.instruments.legal_status.internal_fund',
  SC: 'resources.bo_common.instruments.legal_status.SC',
  ETC: 'resources.bo_common.instruments.legal_status.ETC',
  FIP: 'resources.bo_common.instruments.legal_status.FIP',
  EMTN: 'resources.bo_common.instruments.legal_status.EMTN',
  AD: 'resources.bo_common.instruments.legal_status.AD',
  ACTION: 'resources.bo_common.instruments.legal_status.ACTION',
  DIVERS: 'resources.bo_common.instruments.legal_status.DIVERS',
});

export const fundinfoPricingFrequencyChoices = makeChoicesCommon<FundinfoPricingFrequency>()({
  daily: 'resources.bo_common.instruments.pricing_frequency.daily',
  weekly: 'resources.bo_common.instruments.pricing_frequency.weekly',
  biweekly: 'resources.bo_common.instruments.pricing_frequency.biweekly',
  monthly: 'resources.bo_common.instruments.pricing_frequency.monthly',
  quarterly: 'resources.bo_common.instruments.pricing_frequency.quarterly',
  semi_annual: 'resources.bo_common.instruments.pricing_frequency.semi_annual',
  annual: 'resources.bo_common.instruments.pricing_frequency.annual',
  semi_monthly: 'resources.bo_common.instruments.pricing_frequency.semi_monthly',
  bi_monthly: 'resources.bo_common.instruments.pricing_frequency.bi_monthly',
  ongoing: 'resources.bo_common.instruments.pricing_frequency.ongoing',
});

export const instrumentTypeChoices = makeChoicesCommon<
  InstrumentAssetType | B2bInstrumentAssetType
>()({
  stock: 'resources.bo_common.instruments.asset_type.stock',
  crypto: 'resources.bo_common.instruments.asset_type.crypto',
  etf: 'resources.bo_common.instruments.asset_type.etf',
  'mutual-fund': 'resources.bo_common.instruments.asset_type.mutual-fund',
  'euro-fund': 'resources.bo_common.instruments.asset_type.euro-fund',
  commodity: 'resources.bo_common.instruments.asset_type.commodity',
  eltif: 'resources.bo_common.instruments.asset_type.eltif',
  'structured-product': 'resources.bo_common.instruments.asset_type.structured-product',
  ccb: 'resources.bo_common.instruments.asset_type.ccb',
});

export const instrumentStructuredProductTypeChoices = makeChoicesCommon<StructuredProductType>()({
  autocall: 'resources.bo_common.instruments.structured_product_type.autocall',
  phoenix: 'resources.bo_common.instruments.structured_product_type.phoenix',
  reverse_convertible:
    'resources.bo_common.instruments.structured_product_type.reverse_convertible',
  capital_guaranteed: 'resources.bo_common.instruments.structured_product_type.capital_guaranteed',
  cln: 'resources.bo_common.instruments.structured_product_type.cln',
  amc: 'resources.bo_common.instruments.structured_product_type.amc',
  other: 'resources.bo_common.instruments.structured_product_type.other',
});

export const placementTypeChoices = makeChoicesCommon<AssetPlacementType>()({
  public: 'resources.bo_common.instruments.placement_type.public',
  private: 'resources.bo_common.instruments.placement_type.private',
});
